import React, { useEffect, useMemo, useState } from "react";
import { Aperture, Camera, Clapperboard, Image as ImageIcon, Loader2, Move3D, Search, SlidersHorizontal, Sparkles, Sun } from "lucide-react";
import { fetchPublicVisualReferences } from "../../services/visualReferenceService";
import { DemoTag, TipBox } from "./HelpComponents";

const CATEGORIES = [
  { value: "", label: "全部", icon: ImageIcon },
  { value: "visual_style", label: "画风", icon: Sparkles },
  { value: "lens_focal", label: "焦段", icon: Camera },
  { value: "shot_size", label: "景别", icon: Clapperboard },
  { value: "camera_move", label: "运镜", icon: Move3D },
  { value: "lighting", label: "打光", icon: Sun },
  { value: "frame_rate", label: "帧率/速度", icon: SlidersHorizontal },
  { value: "aperture", label: "光圈", icon: Aperture },
];

const marketLabel = (value) => ({ global: "通用", domestic: "国内", overseas: "出海" }[value] || value || "通用");

const HelpVisualReferences = ({ theme }) => {
  const [filters, setFilters] = useState({ q: "", category: "", market: "" });
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const result = await fetchPublicVisualReferences({ ...filters, mediaType: "image" });
        if (alive) setData(result || { items: [] });
      } catch (e) {
        console.error(e);
        if (alive) setError(e?.message || "视觉参考库加载失败");
      } finally {
        if (alive) setLoading(false);
      }
    }, 160);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [filters.q, filters.category, filters.market]);

  const items = data?.items || [];
  const categoryCounts = useMemo(() => {
    const counts = new Map();
    items.forEach((item) => counts.set(item.category, (counts.get(item.category) || 0) + 1));
    return counts;
  }, [items]);

  return (
    <div className="space-y-5">
      <div
        className="rounded-2xl border p-5"
        style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}
      >
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <DemoTag label="后台生成" color={theme.colors.success} theme={theme} />
          <DemoTag label="只读参考" color={theme.colors.info} theme={theme} />
          <DemoTag label="镜头参数" theme={theme} />
        </div>
        <h2 className="text-xl font-bold mb-2" style={{ color: "var(--color-text-primary)" }}>
          视觉参考库
        </h2>
        <p className="text-sm leading-7 max-w-3xl" style={{ color: "var(--color-text-secondary)" }}>
          这里展示后台已经发布的画风、焦段、景别、运镜、打光、帧率和光圈参考图。前台只展示可用样张，草稿、失败项和未生成图片不会出现在这里。
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[220px_minmax(0,1fr)] gap-4">
        <aside
          className="rounded-2xl border p-3 h-fit"
          style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}
        >
          <div className="px-2 py-2">
            <div className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>参考分类</div>
            <div className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>按镜头参数快速查找。</div>
          </div>
          <div className="space-y-1 mt-2">
            {CATEGORIES.map((category) => {
              const active = filters.category === category.value;
              const Icon = category.icon;
              const count = category.value ? categoryCounts.get(category.value) || 0 : (data?.filteredTotal ?? items.length);
              return (
                <button
                  key={category.value || "all"}
                  type="button"
                  onClick={() => setFilters((prev) => ({ ...prev, category: category.value }))}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm transition-all"
                  style={{
                    backgroundColor: active ? `${theme.colors.accentPrimary}18` : "transparent",
                    border: `1px solid ${active ? `${theme.colors.accentPrimary}66` : "transparent"}`,
                    color: active ? theme.colors.accentPrimary : "var(--color-text-secondary)",
                  }}
                >
                  <span className="flex items-center gap-2">
                    <Icon size={15} />
                    {category.label}
                  </span>
                  <span className="font-mono text-xs">{count}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--color-border-primary)" }}>
            <div className="text-xs px-2 mb-2" style={{ color: "var(--color-text-muted)" }}>项目域</div>
            {[
              ["", "全部"],
              ["global", "通用"],
              ["domestic", "国内"],
              ["overseas", "出海"],
            ].map(([value, label]) => {
              const active = filters.market === value;
              return (
                <button
                  key={value || "all-market"}
                  type="button"
                  onClick={() => setFilters((prev) => ({ ...prev, market: value }))}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: active ? `${theme.colors.accentPrimary}14` : "transparent",
                    color: active ? theme.colors.accentPrimary : "var(--color-text-muted)",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </aside>

        <main className="min-w-0 space-y-4">
          <div
            className="rounded-2xl border p-4 flex flex-wrap items-center gap-3"
            style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}
          >
            <div
              className="flex-1 min-w-[240px] flex items-center gap-2 px-3 py-2 rounded-xl"
              style={{ backgroundColor: "var(--color-bg-tertiary)", border: "1px solid var(--color-border-primary)" }}
            >
              <Search size={16} style={{ color: "var(--color-text-muted)" }} />
              <input
                value={filters.q}
                onChange={(event) => setFilters((prev) => ({ ...prev, q: event.target.value }))}
                placeholder="搜索画风、焦段、提示标签"
                className="flex-1 bg-transparent outline-none text-sm"
                style={{ color: "var(--color-text-primary)" }}
              />
            </div>
            <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              {loading ? (
                <span className="inline-flex items-center gap-1"><Loader2 size={13} className="animate-spin" /> 加载中</span>
              ) : (
                <>共 {data?.filteredTotal ?? items.length} 张</>
              )}
            </div>
          </div>

          {error && (
            <TipBox theme={theme} type="warning">
              {error}
            </TipBox>
          )}

          {!loading && !items.length ? (
            <div
              className="rounded-2xl border p-10 text-center"
              style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)" }}
            >
              还没有可展示的参考图。请先在后台生成图片，并将条目状态设为“可用/已发布”。
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
              {items.map((item) => {
                const image = item.thumbnailUrl || item.referenceUrl;
                return (
                  <article
                    key={item.id}
                    className="rounded-2xl border overflow-hidden"
                    style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}
                  >
                    <div className="aspect-video overflow-hidden" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                      <img src={image} alt={item.name || "视觉参考图"} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="p-4">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <DemoTag label={item.categoryLabel || item.category} theme={theme} />
                        <DemoTag label={marketLabel(item.market)} color={theme.colors.info} theme={theme} />
                      </div>
                      <h3 className="text-sm font-bold leading-snug" style={{ color: "var(--color-text-primary)" }}>
                        {item.name}
                      </h3>
                      {item.nameEn && (
                        <div className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>{item.nameEn}</div>
                      )}
                      <p className="text-xs leading-6 mt-3 line-clamp-3" style={{ color: "var(--color-text-secondary)" }}>
                        {item.summary || item.usageScene || "暂无说明"}
                      </p>
                      {item.tags && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {String(item.tags).split(/[，,]/).map((tag) => tag.trim()).filter(Boolean).slice(0, 5).map((tag) => (
                            <span
                              key={tag}
                              className="text-[11px] px-2 py-1 rounded-full"
                              style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" }}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default HelpVisualReferences;
